import {
    ChangeEmailReqApi,
    ChangeNameReqApi,
    ChangePasswordReqApi,
    ChangePhoneReqApi,
    FacilityApi,
    FacilityParkerBalanceApi,
    InvoiceApi,
    ListedPlanApi,
    LogInReqApi,
    OperatorApi,
    OrderApi,
    OrderDetailsApi,
    OrderPayInitReqApi,
    OrderPayInitRespApi,
    ParkerApi,
    PaymentApi,
    QuantityUpdateReqApi,
    ReservationApi,
    ReserveConfirmReqApi,
    ReserveInitReqApi,
    ReserveInitRespApi,
    ReservePreviewRespApi,
    ResetCodeCheckReqApi,
    ResetPasswordReqApi,
    SettleInitRespApi,
    SignUpReqApi,
    SubListEntryApi,
    SubscribeConfirmReqApi,
    SubscribeConfirmRespApi,
    SubscribeInitReqApi,
    SubscribeInitRespApi,
    SubscribeInviteInitReqApi,
    SubscribeInviteInitRespApi,
    SubscribeUpdateReqApi,
    SubscriptionApi,
    SubscriptionParkerApi,
    SubscriptionParkerChangeReqApi,
    SubscriptionParkerInviteReqApi,
    SubscriptionParkerRemoveReqApi,
    SubscriptionStatus,
    VehicleAddReqApi,
    VehicleDeleteReqApi} from "./ApiTransport";
import { ApiUrl } from "./ApiUrl";
import { OkResult, Result } from "./Result";
import { ParkerPlanInvite, PaymentMethodResp, SettleInitReq, SubscribeInviteInitReq, SubscribeInvitePreviewReq, SubscribePreviewReq, ModifySubscriptionPreviewReq, Vehicle, ListedPlan } from "./ApiTypes";
import { parseParkerPlanInvite, parseReservePreviewResp, parseSubscribeInitResp, parseSubscribeInviteInitResp, parseSubscribeInvitePreviewResp, parseSubscribePreviewResp, parseSubscribeUpdateDecreaseResp, parseSubscribeUpdateDeferResp, parseSubscriptionGuest, parseVehicleLinkResp, parseListedPlan, parseApplyCouponToPlanResp } from "./ApiParse";
import { emitSubscribeInviteInitReq, emitSubscribeInvitePreviewReq, emitSubscribePreviewReq, emitModifySubscriptionPreviewReq } from "./ApiEmit";
import {checkExpiration} from "./../shared/apiConfig"
import { clearLocalStoragePreserving, STORAGE_GROUPS } from "../shared/localStorageHelper";
import { authAtom } from "./Atoms";
import { useAtom } from "jotai";
import { beginGlobalLoading } from "./LoadingManager";

export const notSet = ( a: any ) => a === null;

export type SignUpResp         = ParkerApi;
export type LogInResp          = ParkerApi;
export type ChgPassResp        = ParkerApi;
export type ChgNameResp        = ParkerApi;
export type ChgPhoneResp       = ParkerApi;
export type ChgEmailResp       = ParkerApi;
export type ResetPassResp      = ParkerApi;
export type ResetCodeCheckResp = ParkerApi;

export interface ForgotPassReq  { emailAddress: string; }
export type ForgotPassResp = string;

export interface StripeConfigResp {
    publishableKey: string;
    stripeAccount:  string;
};

export interface SetupIntentCreateResp {
    clientSecret:                string;
    customerSessionClientSecret: string;
}

export interface PaymentIntentCreateResp {
    clientSecret:  string;
}

export interface VehicleData {
    make:               string;
    model:              string;
    color:              string;
    stateCode:          string;
    licensePlateNumber: string;
}

const parseArray = <TValApi, TVal>( func: ( api: TValApi ) => TVal ) => ( x: TValApi[] ) => x.map( func );

const getArgs = (hasToken = true): RequestInit => {
    return {
        method:      "GET",
        credentials: "include",
        headers:     getCustomHeaders()
    }
}

const jsonArgs = ( verb: string, payload: any, hasToken = true ): RequestInit => {
    return {
        method:      verb,
        headers:     {
            "Content-Type": "application/json",
            ...getCustomHeaders()
        },
        body:        JSON.stringify( payload ),
        credentials: "include"
    }
}

const postArgs = ( payload: any, hasToken = true ) => jsonArgs( "POST",   payload, hasToken );
const resOk    = <T>( value: T ): OkResult<T>          => ({ isOk: true, value: value })
const ok       = <T>( value: T ): Promise<OkResult<T>> => Promise.resolve( resOk( value ) );
const okDelay = <TErr, TVal>( value: TVal ): Promise<Result<TErr, TVal>> =>
    new Promise( ( resolve ) =>
        setTimeout( () => { resolve( resOk( value ) ); }, 500 )
);

const getCustomHeaders = (): HeadersInit => {
    // Extract subdomain from current URL
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    let headers = {
        'X-Customer-Subdomain': subdomain
    } as any;
    const accessToken =(() => {
        try {
            const authTokenStr = localStorage.getItem("authToken");
            if (authTokenStr){
                const authToken = JSON.parse(authTokenStr!);
                if (checkExpiration(authToken.accessToken)) throw new Error("Token expired");
                return authToken.accessToken; 
            }
            return null
        } catch {
          clearLocalStoragePreserving([STORAGE_GROUPS.helpDialogs]);
          window.location.href = '/login';  
        }
    })();
    
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    try {
        const actingAsRaw = localStorage.getItem("actingAs");
        if (actingAsRaw) {
            const actingAs = JSON.parse(actingAsRaw);
            if (actingAs?.parkerId) {
                headers['X-Acting-As-Parker'] = actingAs.parkerId.toString();
            }
        }
    } catch { /* ignore */ }

    return headers;
}

const api = <TErr, TVal>( url: string, init: RequestInit = {}, hasToken = true ): Promise<Result<TErr, TVal>> => {
    // Merge custom headers with any existing headers
    const headers = {
        ...getCustomHeaders(),
        ...init.headers
    };
    const end = beginGlobalLoading();

    return fetch( url, { ...init, headers } )
        .then( response => {
            const json   = response.json();
            if(response.status == 401){
                window.location.href = '/login'
            }
            const isOk = (response.status == 200);
            return json.then( (data: any) => {
                if( isOk ) {
                    return { isOk: true as const, value: data as TVal } as Result<TErr, TVal>;
                }
                return { isOk: false as const, error: data as TErr } as Result<TErr, TVal>;
            } );
        } )
        .finally(() => { if (end) end(); });
}


const api2 = <TValApi, TVal>( url: string, init: RequestInit = {}, f: (x: TValApi) => TVal, hasToken = true ): Promise<Result<string, TVal>> => {
    // Merge custom headers with any existing headers
    const headers = {
        ...getCustomHeaders(),
        ...init.headers
    };
    const end = beginGlobalLoading();

    return fetch( url, { ...init, headers } )
        .then( response => {
            const json = response.json();
            if(response.status == 401){
                window.location.href = '/login'
            }
            const isOk = ( response.status == 200 );
            return json.then( (data: any) => {
                if( isOk ) {
                    const parsed = f( data as TValApi );
                    return { isOk: true as const, value: parsed } as Result<string, TVal>;
                }
                return { isOk: false as const, error: data as string } as Result<string, TVal>;
            } );
        } )
        .finally(() => { if (end) end(); });
}

export class Api {

    //operator
    static opDetails() { return api<string, OperatorApi>( ApiUrl.opDetails(), getArgs(false), false ); }

    //auth
    static amILoggedIn        = (                           ) => api<string, LogInResp>         ( ApiUrl.amILoggedIn(),        getArgs(      ) );
    static signUp             = ( req: SignUpReqApi         ) => api<string, SignUpResp>        ( ApiUrl.signUp(),             postArgs( req ) );
    static logIn              = ( req: LogInReqApi          ) => api<string, LogInResp>         ( ApiUrl.logIn(),              postArgs( req ) );
    static logOut             = (                           ) => api<string, LogInResp>         ( ApiUrl.logOut(),             postArgs( {}  ) );
    static forgotPassword     = ( req: ForgotPassReq        ) => api<string, ForgotPassResp>    ( ApiUrl.forgotPassword(),     postArgs( req) );    
    static resetCodeCheck     = ( req: ResetCodeCheckReqApi ) => api<string, ResetCodeCheckResp>( ApiUrl.resetCodeCheck(),     postArgs( req) );
    static resetPassword      = ( req: ResetPasswordReqApi  ) => api<string, ResetPassResp>     ( ApiUrl.resetPassword(),      postArgs( req) );    
    static changeName         = ( req: ChangeNameReqApi     ) => api<string, ChgNameResp>       ( ApiUrl.changeName(),         postArgs( req ) );
    static changePhoneNumber  = ( req: ChangePhoneReqApi    ) => api<string, ChgPhoneResp>      ( ApiUrl.changePhoneNumber(),  postArgs( req ) );
    static changeEmailAddress = ( req: ChangeEmailReqApi    ) => api<string, ChgEmailResp>      ( ApiUrl.changeEmailAddress(), postArgs( req ) );    
    static changePassword     = ( req: ChangePasswordReqApi ) => api<string, ChgPassResp>       ( ApiUrl.changePassword(),     postArgs( req ) );

    //facilities
    static facilitySearch    = (            ) => api<string, FacilityApi[] >( ApiUrl.facilitySearch(),      /* no need to log in */   );
    static facilityDetails   = ( id: number, showAllPlans?: boolean ) => api<string, FacilityApi   >( ApiUrl.facilityDetails( id, showAllPlans ),  );

    //stripe
    static stripeConfig              = ()             => api<string, StripeConfigResp>   ( ApiUrl.stripeConfig(),                   postArgs( {} ) );
    static stripePaymentMethodList   = ()             => api<string, PaymentMethodResp[]>( ApiUrl.stripePaymentMethodList(),        getArgs()      );
    static stripePaymentMethodGet    = ( id: string ) => api<string, PaymentMethodResp  >( ApiUrl.stripePaymentMethodDetails( id ), getArgs()      );
    static stripePaymentMethodDelete = ( id: string ) => api<string, string>   ( ApiUrl.stripePaymentMethodDelete( id ),   postArgs( {} ) );

    //vehicles
    static vehicleList    = ()                       => api<string, Vehicle[]>( ApiUrl.vehicleList(),      getArgs() );
    static vehicleListByParker    = (parkerId: number) => api<string, Vehicle[]>( ApiUrl.vehicleListByParker(parkerId),      getArgs() );
    static vehicleGet     = ( id: number )           => api<string, Vehicle>  ( ApiUrl.vehicleDetails(id), getArgs() );
    static vehicleAdd     = ( vehicle: VehicleData ) => api<string, Vehicle>  ( ApiUrl.vehicleAdd(),       postArgs( vehicle ) );
    static vehicleDelete  = ( id: number        )    => api<string, Vehicle>  ( ApiUrl.vehicleDelete(id),  postArgs( {} ) );

    //reservations
    static reservationList     = ()             => api<string, ReservationApi[]>( ApiUrl.reservationList(),        getArgs(     ) );
    static reservationDetails  = ( id: number ) => api<string, ReservationApi>  ( ApiUrl.reservationDetails( id ), getArgs(     ) );
    static reservationCancel   = ( id: number ) => api<string, any>             ( ApiUrl.reservationCancel( id ),  postArgs( {} ) );

    static reservationInit( req: ReserveInitReqApi ) {
        return api<string, ReserveInitRespApi>( ApiUrl.reservationInit(), postArgs( req ) ); }

    static reservationPreview( req: ReserveInitReqApi ) {
        return api2( ApiUrl.reservationPreview(), postArgs( req ), parseReservePreviewResp ); }

    static reservationConfirm( req: ReserveConfirmReqApi ) {
        return api<string, ReserveInitRespApi>( ApiUrl.reservationConfirm(), postArgs( req ) ); }

    static subscriptionPreview( planId: number, req: SubscribePreviewReq ) {
        return api2( ApiUrl.subscribePreview( planId ), postArgs( emitSubscribePreviewReq( req ) ), parseSubscribePreviewResp ); }

    static modifySubscriptionPreview( subscriptionId: number, req: ModifySubscriptionPreviewReq ) {
        return api2( ApiUrl.modifySubscriptionPreview( subscriptionId ), postArgs( emitModifySubscriptionPreviewReq( req ) ), parseSubscribePreviewResp ); }

    static subscriptionInvitePreview( planId: number, req: SubscribeInvitePreviewReq ) {
        return api2( ApiUrl.subscribeInvitePreview( planId ), postArgs( emitSubscribeInvitePreviewReq( req ) ), parseSubscribeInvitePreviewResp ); }    

    //plans and subscriptions
    static subscriptionInit( req: SubscribeInitReqApi ) {
        return api<string, SubscribeInitRespApi>( ApiUrl.subscriptionInit(), postArgs( req ) ); }

    static subscriptionInviteInit( req: SubscribeInviteInitReq ) {
        return api2( ApiUrl.subscriptionInviteInit(), postArgs( emitSubscribeInviteInitReq( req ) ), parseSubscribeInviteInitResp ); }

    static subscriptionUpdateInit(  req: SubscribeUpdateReqApi ) { return api2( ApiUrl.subscriptionUpdateInit(),  postArgs( req ), parseSubscribeInitResp           ); }
    static subscriptionUpdateDecr(  req: SubscribeUpdateReqApi ) { return api2( ApiUrl.subscriptionUpdateDecr(),  postArgs( req ), parseSubscribeUpdateDecreaseResp ); }
    static subscriptionUpdateNextCycle( req: QuantityUpdateReqApi ) { return api2( ApiUrl.subscriptionUpdateNextCycle(),  postArgs( req ), x => {} ); }
    static subscriptionUpdateDefer( req: SubscribeUpdateReqApi ) { return api2( ApiUrl.subscriptionUpdateDefer(), postArgs( req ), parseSubscribeUpdateDeferResp    ); }
    static subscriptionUpdateEnhanced( req: SubscribeUpdateReqApi ) { return api<string, string>( ApiUrl.subscriptionUpdateEnhanced(), postArgs( req ) ); }

    static subscriptionConfirm( req: SubscribeConfirmReqApi ) {
        return api<string, SubscribeConfirmRespApi>( ApiUrl.subscriptionConfirm(), postArgs( req ) ); }

    static changeSubscriptionStatus( subscriptionId: number, status: SubscriptionStatus, isAccountsReceivable?: boolean) {
        return api<string, string>( ApiUrl.changeSubscriptionStatus( subscriptionId, status, isAccountsReceivable ), postArgs( {} ) ); }

    static subscriptionList(queryParams?: Record<string, string>) {
        return api<string, SubListEntryApi[]>(ApiUrl.subscriptionList(queryParams), getArgs());
    }

    static subscriptionDetails( id: number )           { return api<string, SubscriptionApi>(   ApiUrl.subscriptionDetails( id ), getArgs() ); }
    static parkerSubscriptionsDetails()                { return api<string, SubscriptionApi[]>(   ApiUrl.parkerSubscriptionDetails(), getArgs() ); }
    static subscriptionGuestDetails( id: number )      { return api2( ApiUrl.subscriptionGuestDetails( id ), getArgs(), parseSubscriptionGuest ); }
    static subscriptionSetupPayment( id: number )      { return api<string, SetupIntentCreateResp>( ApiUrl.subscriptionSetupPayment( id ), postArgs( {} ) ); }
    static planTermsGet(  planId: number )             { return api<string, string>( ApiUrl.planTerms( planId ), getArgs() ); }
    static planInviteGet( parkerPlanInviteId: number ) { return api2( ApiUrl.planInviteDetails( parkerPlanInviteId ), getArgs(), parseParkerPlanInvite ); }
    static usageBasedInviteSetupPayment( inviteId: number ) { return api<string, SetupIntentCreateResp>( ApiUrl.usageBasedInviteSetupPayment( inviteId ), postArgs( {} ) ); }
    static getPlan( planId: number )                   { return api2( ApiUrl.getPlan( planId ), getArgs(), parseListedPlan ); }    
    static applyCouponToPlan( planId: number, couponCode: string ) { return api2 ( ApiUrl.applyCouponToPlan( planId ), postArgs({couponCode}), parseApplyCouponToPlanResp );}    
    
    //sub-parker management
    static subParkerInvite( subscriptionId: number, req: SubscriptionParkerInviteReqApi )
        { return api<string, SubscriptionParkerApi>( ApiUrl.subParkerCreate( subscriptionId ), postArgs( { ...req, subscriptionGroupId: req.subscriptionGroupId ?? null } ) ); }

    static subParkerBatchInvite( subscriptionId: number, reqs: SubscriptionParkerInviteReqApi[] ) {
        const body = reqs.map( ( req ) => ( { ...req, subscriptionGroupId: req.subscriptionGroupId ?? null } ) );
        return api<string, SubscriptionParkerApi[]>( ApiUrl.subParkerBatchInvite( subscriptionId ), postArgs( body ) );
    }

    static subParkerChange( subscriptionId: number, req: SubscriptionParkerChangeReqApi )
        { return api<string, boolean>( ApiUrl.subParkerUpdate( subscriptionId ), postArgs( req ) ); }

    static subParkerRemove( subscriptionId: number, req: SubscriptionParkerRemoveReqApi )
        { return api<string, boolean>( ApiUrl.subParkerDelete( subscriptionId ), postArgs( req ) ); }

    //subscription vehicle management
    static subscriptionVehicleAdd( subscriptionId: number, req: VehicleAddReqApi )
        { return api2( ApiUrl.subscriptionVehicleAdd( subscriptionId), postArgs(req), parseVehicleLinkResp ); }

    static subscriptionVehicleDelete( subscriptionId: number, vehicleId: number, req: VehicleDeleteReqApi )
        { return api( ApiUrl.subscriptionVehicleDelete( subscriptionId, vehicleId), postArgs(req) ); }

    //finance endpoints
    static facilityParkerBalanceList(            ) { return api<string, FacilityParkerBalanceApi[]>( ApiUrl.facilityParkerBalanceList(), getArgs()       ); }
    static orderPayInit( req: OrderPayInitReqApi ) { return api<string, OrderPayInitRespApi>(        ApiUrl.orderPayInit(),              postArgs( req ) ); }
    static settleInit  ( req: SettleInitReq      ) { return api<string, SettleInitRespApi>(          ApiUrl.settleInit(),                postArgs( req ) ); }

    //orders
    static orderList()                  { return api<string, OrderApi[]>     ( ApiUrl.orderList(),      getArgs() ); }
    static orderDetails( id: number )   { return api<string, OrderDetailsApi>( ApiUrl.orderDetails(id), getArgs() ); }

    //invoices
    static invoiceList()                { return api<string, InvoiceApi[]> ( ApiUrl.invoiceList(),        getArgs() ); }
    static invoiceDetails( id: number ) { return api<string, InvoiceApi>   ( ApiUrl.invoiceDetails( id ), getArgs() ); }

    //payments
    static paymentList()                { return api<string, PaymentApi[]> ( ApiUrl.paymentList(),        getArgs() ); }
    static paymentDetails( id: number ) { return api<string, PaymentApi>   ( ApiUrl.paymentDetails( id ), getArgs() ); }
}